import { useEffect } from "react";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import GeoPoint from "@/model/GeoPoint";
import "@/components/LocationPicker/LocationPicker.scss";

const WORLD_CENTER: [number, number] = [30, 0];
const WORLD_ZOOM = 2;
const PICKED_ZOOM = 15;

interface LocationPickerProps {
  value: GeoPoint | null;
  /** Omit to render a read-only map. */
  onChange?: (point: GeoPoint) => void;
  height?: number;
}

interface MapClickHandlerProps {
  onChange: (point: GeoPoint) => void;
}

function MapClickHandler({ onChange }: MapClickHandlerProps) {
  useMapEvents({
    click(event) {
      onChange({ latitude: event.latlng.lat, longitude: event.latlng.lng });
    },
  });
  return null;
}

interface MapRecenterProps {
  value: GeoPoint | null;
}

/** Keeps the map centred on the value when it changes from outside (e.g. an address lookup). */
function MapRecenter({ value }: MapRecenterProps) {
  const map = useMap();

  useEffect(() => {
    if (value) {
      map.setView(
        [value.latitude, value.longitude],
        Math.max(map.getZoom(), PICKED_ZOOM)
      );
    }
  }, [map, value]);

  return null;
}

function LocationPicker({ value, onChange, height = 320 }: LocationPickerProps) {
  const center: [number, number] = value
    ? [value.latitude, value.longitude]
    : WORLD_CENTER;

  return (
    <div
      className={`location-picker${onChange ? " location-picker--editable" : ""}`}
      style={{ height }}
    >
      <MapContainer
        center={center}
        zoom={value ? PICKED_ZOOM : WORLD_ZOOM}
        scrollWheelZoom={Boolean(onChange)}
        className="location-picker__map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {value && (
          <CircleMarker
            center={[value.latitude, value.longitude]}
            radius={10}
            pathOptions={{ color: "#a2231d", fillColor: "#c8382f", fillOpacity: 0.8 }}
          />
        )}
        {onChange && <MapClickHandler onChange={onChange} />}
        <MapRecenter value={value} />
      </MapContainer>
    </div>
  );
}

export default LocationPicker;
export type { LocationPickerProps };
